import type { ReactNode } from 'react';

import {
  Callout,
  Code,
  CodeBlock,
  DocTable,
  H3,
  OL,
  P,
  Section,
  UL,
} from '@/components/docs/primitives';

export interface DocPage {
  id: string;
  title: string;
  group: string;
  /** Plain-text blob used by the docs search box. */
  keywords: string;
  render: () => ReactNode;
}

const PHONE_NUMBER_ID = '1324049317454825';
const WABA_ID = '1366667627811627';
const APP_ID = '1740110457043143';
const SENDER = '+91 81478 25069';

/* ─────────────────────────── Overview ─────────────────────────── */

const overview: DocPage = {
  id: 'overview',
  title: 'What this service does',
  group: 'Getting started',
  keywords: 'overview architecture queue worker cron erp minio postgres redis',
  render: () => (
    <Section id="overview" title="What this service does">
      <P>
        This service sends WhatsApp template messages — subscription reminders and fleet reports —
        to customers who have opted in, through Meta&rsquo;s Cloud API. An ERP cron job calls the
        API; this service is the queue, the scheduler, the consent record and the delivery ledger.
      </P>
      <P>
        It is deliberately one-way. Customers never start a conversation, which means every message
        is business-initiated and <strong>must</strong> use a template Meta has approved. Free-form
        text is only possible in the 24 hours after a customer messages you first.
      </P>

      <H3>The path a message takes</H3>
      <CodeBlock>{`ERP cron ──POST /v1/messages──▶ API
                                 │ validate: API key, template APPROVED,
                                 │ recipient opted in, MIME ok, idempotency
                                 │ insert row (status=queued)
                                 ▼
                             "send" queue (Redis)
                                 │
                                 ▼
                             Worker (concurrency 1, 5/s)
                                 │ 1. re-check consent
                                 │ 2. re-check template status
                                 │ 3. unique-recipient cap guard
                                 │ 4. media: MinIO → SHA-256 → upload to Meta
                                 │ 5. POST /{PHONE_NUMBER_ID}/messages
                                 ▼
                             Meta Cloud API ──▶ customer's WhatsApp
                                 │
                                 ▼ webhook (statuses / inbound)
                             POST /webhooks/whatsapp
                                 │ verify signature, update status,
                                 │ inbound "STOP" → opt out
                                 ▼
                             Postgres ──▶ this dashboard`}</CodeBlock>

      <Callout tone="info" title="The database insert always happens before the enqueue">
        If the process dies between the two, a <Code>queued</Code> row exists with no job. A
        maintenance task runs every 15 minutes and re-enqueues those orphans, so nothing is lost
        between accepting a message and sending it.
      </Callout>
    </Section>
  ),
};

const account: DocPage = {
  id: 'account',
  title: 'The WhatsApp account',
  group: 'Getting started',
  keywords: 'meta account waba phone number id app id sender display name verification tier',
  render: () => (
    <Section id="account" title="The WhatsApp account">
      <P>
        Messages are sent from a single registered business number. There is no per-message
        &ldquo;from&rdquo; field — the sender is fixed by which Phone Number ID the service calls.
      </P>
      <DocTable
        head={['What', 'Value']}
        rows={[
          ['Sender number', <Code key="s">{SENDER}</Code>],
          ['Display name recipients see', 'Pragathi Solutions'],
          ['Phone Number ID', <Code key="p">{PHONE_NUMBER_ID}</Code>],
          ['WhatsApp Business Account ID', <Code key="w">{WABA_ID}</Code>],
          ['Meta App ID', <Code key="a">{APP_ID}</Code>],
        ]}
      />

      <Callout tone="warn" title="Messaging limit">
        Until the business portfolio passes verification you can reach{' '}
        <strong>250 unique recipients per rolling 24 hours</strong>. Messaging the same person twice
        in that window costs one slot, not two. The worker defers over-cap messages by an hour
        rather than failing them, so they roll into the next day.
      </Callout>

      <H3>Health</H3>
      <P>
        The Overview page reports what Meta says about the account. If sending stops working,
        that panel is the first place to look — it surfaces the exact entity that is blocking, be
        it the WABA, the business, the number or the app.
      </P>
    </Section>
  ),
};

/* ─────────────────────────── Templates ─────────────────────────── */

const templates: DocPage = {
  id: 'templates',
  title: 'Templates',
  group: 'Sending',
  keywords: 'template approval pending rejected placeholder utility marketing category submit meta',
  render: () => (
    <Section id="templates" title="Templates">
      <P>
        Every business-initiated message uses a template that Meta has reviewed and approved.
        A template has fixed text plus numbered placeholders — <Code>{'{{1}}'}</Code>,{' '}
        <Code>{'{{2}}'}</Code> — that you fill at send time.
      </P>

      <H3>Statuses</H3>
      <DocTable
        head={['Status', 'Can you send with it?', 'What to do']}
        rows={[
          ['APPROVED', 'Yes', 'Nothing.'],
          ['PENDING', 'No', 'Wait. Review is usually minutes to a day.'],
          ['REJECTED', 'No', 'Fix the wording and submit a new version.'],
          ['PAUSED', 'No', 'Meta paused it for poor quality — customers blocked or reported you.'],
          ['DISABLED', 'No', 'Permanently off. Write a replacement.'],
        ]}
      />

      <H3>Submitting from this dashboard</H3>
      <P>
        Templates → <strong>Submit to Meta</strong> lists the catalogue this service ships with.
        Tick the ones you want and submit. Admin only, because each submission consumes a slot
        against the account&rsquo;s template limit and cannot be cancelled once sent.
      </P>

      <H3>Rules that avoid rejection</H3>
      <UL>
        <li>Category must be UTILITY: transactional only. No offers, discounts or marketing tone.</li>
        <li>
          Placeholders are positional. <Code>{'{{1}}'}</Code> is valid;{' '}
          <Code>{'{{ProductName}}'}</Code> is not and will be rejected.
        </li>
        <li>The body must not start or end with a placeholder.</li>
        <li>Every placeholder needs a realistic sample value at submission.</li>
        <li>
          Values that never change — a support number, a portal URL — belong in the fixed text, not
          in a variable. A bare variable URL in the body is a common rejection reason.
        </li>
        <li>A DOCUMENT or IMAGE header needs a sample file uploaded with the submission.</li>
      </UL>

      <Callout tone="danger" title="hello_world will never work">
        Every account gets a <Code>hello_world</Code> template that shows as APPROVED. Meta blocks
        it from real business numbers with error <Code>131058</Code> — it only works from the
        sandbox test number in the app dashboard. It is not a useful test of your setup.
      </Callout>
    </Section>
  ),
};

const sending: DocPage = {
  id: 'sending',
  title: 'Sending messages',
  group: 'Sending',
  keywords: 'send message ui cron batch idempotency queue preview parameters',
  render: () => (
    <Section id="sending" title="Sending messages">
      <H3>From this dashboard</H3>
      <P>
        Messages → <strong>Send message</strong>. Pick an approved template, enter the recipient,
        fill one value per placeholder, and the preview shows exactly what will arrive. It queues
        through the same path the ERP cron uses, so consent, template status and the cap are all
        still enforced.
      </P>

      <H3>From the ERP cron</H3>
      <CodeBlock lang="ts">{`const items = reports.map(r => ({
  idempotencyKey: \`report-\${r.period}-\${r.customerId}\`,
  to: r.phoneE164,
  template: "fleet_report_document",
  bodyParams: [r.customerName, r.reportName, r.fromDate, r.toDate],
  document: { s3Uri: r.s3Uri, filename: \`\${r.reportName}.pdf\` },
  correlationId: runId,
  metadata: { customerId: r.customerId }
}));

for (const chunk of chunks(items, 200)) {
  const res = await fetch(\`\${WA_SERVICE}/v1/messages/batch\`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-API-Key": KEY },
    body: JSON.stringify({ messages: chunk })
  });
  const { results } = await res.json();
  results.filter(r => r.error).forEach(r =>
    log.warn("enqueue failed", chunk[r.index], r.error));
}`}</CodeBlock>

      <Callout tone="ok" title="Re-running the cron is safe">
        <Code>idempotencyKey</Code> is unique. Sending the same key twice returns the original
        message with <Code>duplicate: true</Code> instead of sending again, so a cron that retries
        after a partial failure never double-messages a customer.
      </Callout>

      <H3>Queued is not sent</H3>
      <P>
        The API accepts and queues; the worker sends. A message moves{' '}
        <Code>queued → processing → sent → delivered → read</Code>, and the last two only arrive if
        webhooks are configured. Statuses can arrive out of order and are never downgraded.
      </P>
    </Section>
  ),
};

/* ─────────────────────────── Consent ─────────────────────────── */

const consent: DocPage = {
  id: 'consent',
  title: 'Consent',
  group: 'Sending',
  keywords: 'consent opt in opt out stop unsubscribe gdpr record source note history',
  render: () => (
    <Section id="consent" title="Consent">
      <Callout tone="warn" title="This service is the source of truth for consent">
        A message to a number that is not opted in is rejected with{' '}
        <Code>422 RECIPIENT_NOT_OPTED_IN</Code>. Recipients are never auto-created — consent has to
        be recorded first, deliberately.
      </Callout>

      <H3>What counts as consent</H3>
      <P>
        The customer must have clearly agreed to receive messages <strong>on WhatsApp</strong> from
        your brand, and the record must be attributable: a timestamp and a source. Keep the form,
        email or signed document that backs it up.
      </P>

      <H3>Opting out</H3>
      <UL>
        <li>
          A customer replying <Code>STOP</Code> or <Code>UNSUBSCRIBE</Code> is opted out
          automatically by the webhook, and anything still queued for them is cancelled.
        </li>
        <li>
          Staff can opt someone out from Recipients. A reason is mandatory and is written to the
          consent history.
        </li>
        <li>
          Every consent change writes both a consent event (the customer-facing record) and an
          audit row (who did it in our system).
        </li>
      </UL>

      <H3>Importing</H3>
      <P>
        Recipients → CSV import takes <Code>phone,name,externalRef,consentedAt</Code>. Someone who
        has already opted out is skipped, never silently re-subscribed.
      </P>
    </Section>
  ),
};

/* ─────────────────────────── API ─────────────────────────── */

const apiRef: DocPage = {
  id: 'api',
  title: 'API reference',
  group: 'Reference',
  keywords: 'api endpoints rest curl authentication api key jwt messages recipients templates health',
  render: () => (
    <Section id="api" title="API reference">
      <P>
        Base path <Code>/v1</Code>. Machines authenticate with <Code>X-API-Key</Code>; dashboard
        users with <Code>Authorization: Bearer &lt;jwt&gt;</Code>. Phone numbers are E.164.
      </P>

      <H3>Messages</H3>
      <DocTable
        head={['Endpoint', 'Purpose']}
        rows={[
          [<Code key="a">POST /v1/messages</Code>, 'Queue one message. 202, or 200 if a duplicate key.'],
          [<Code key="b">POST /v1/messages/batch</Code>, 'Up to 500 at once. Returns 207 with a per-item result.'],
          [<Code key="c">GET /v1/messages</Code>, 'List with filters: status, to, template, correlationId, dates.'],
          [<Code key="d">GET /v1/messages/:id</Code>, 'One message plus its full event timeline.'],
          [<Code key="e">POST /v1/messages/:id/retry</Code>, 'Re-queue a failed message.'],
          [<Code key="f">POST /v1/messages/:id/cancel</Code>, 'Cancel while still queued.'],
        ]}
      />

      <H3>Queue a message</H3>
      <CodeBlock lang="bash">{`curl -X POST https://<host>/v1/messages \\
  -H "X-API-Key: $WA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "idempotencyKey": "reminder-2026-09-KA12KH1234",
    "to": "+919876543210",
    "template": "subscription_expiry_reminder",
    "bodyParams": ["Saarthi", "KA12KH1234", "30"],
    "correlationId": "cron-2026-09-09",
    "metadata": { "customerId": "CUST1042" }
  }'`}</CodeBlock>

      <H3>Recipients</H3>
      <DocTable
        head={['Endpoint', 'Purpose']}
        rows={[
          [<Code key="a">POST /v1/recipients</Code>, 'Create with recorded consent.'],
          [<Code key="b">GET /v1/recipients</Code>, 'List and search.'],
          [<Code key="c">GET /v1/recipients/:phone</Code>, 'One recipient, consent history, recent messages.'],
          [<Code key="d">POST /v1/recipients/:phone/opt-in</Code>, 'Record consent. Source required.'],
          [<Code key="e">POST /v1/recipients/:phone/opt-out</Code>, 'Opt out and cancel queued. Reason required.'],
          [<Code key="f">POST /v1/recipients/import</Code>, 'CSV bulk import. Admin only.'],
        ]}
      />

      <H3>Other</H3>
      <DocTable
        head={['Endpoint', 'Purpose']}
        rows={[
          [<Code key="a">GET /v1/health/live</Code>, 'Liveness. No dependencies. Used by Docker and uptime checks.'],
          [<Code key="b">GET /v1/health</Code>, 'Full diagnostics. Always 200 so the dashboard can render it.'],
          [<Code key="c">GET /v1/templates</Code>, 'Cached template list with approval status.'],
          [<Code key="d">POST /v1/templates/sync</Code>, 'Pull the latest statuses from Meta.'],
          [<Code key="e">POST /v1/templates/submit</Code>, 'Submit catalogue templates for review. Admin only.'],
        ]}
      />

      <H3>Errors</H3>
      <CodeBlock lang="json">{`{
  "error": {
    "code": "RECIPIENT_NOT_OPTED_IN",
    "message": "Recipient is not opted in to receive messages",
    "details": { "phone": "+919876543210" },
    "requestId": "0b1c4cb3-a26e-40d3-9be2-f366cd41d926"
  }
}`}</CodeBlock>
      <P>
        Quote the <Code>requestId</Code> when reporting a problem — it appears on the matching log
        line.
      </P>
    </Section>
  ),
};

const errors: DocPage = {
  id: 'errors',
  title: 'Error codes',
  group: 'Reference',
  keywords: 'error codes 131026 131058 130429 132000 190 141006 141010 troubleshooting failed',
  render: () => (
    <Section id="errors" title="Error codes">
      <H3>From this service</H3>
      <DocTable
        head={['Code', 'Meaning']}
        rows={[
          ['RECIPIENT_NOT_OPTED_IN', 'No consent on record. Add it before sending.'],
          ['TEMPLATE_NOT_APPROVED', 'The template is PENDING, REJECTED, PAUSED or DISABLED.'],
          ['TEMPLATE_PARAM_MISMATCH', 'Wrong number of values for the template placeholders.'],
          ['UNSUPPORTED_MEDIA_TYPE', 'WhatsApp does not accept that file type. CSV is not supported.'],
          ['MEDIA_TOO_LARGE', 'Over 100 MB for documents, 5 MB for images.'],
          ['ALLOWLIST_BLOCKED', 'Non-production environment: the recipient is not on the send allow-list.'],
        ]}
      />

      <H3>From Meta</H3>
      <DocTable
        head={['Code', 'Meaning', 'What the worker does']}
        rows={[
          ['131026', 'Recipient cannot receive — not on WhatsApp, or blocked you.', 'Fails permanently. Retrying wastes quota.'],
          ['131058', 'hello_world only works from Meta test numbers.', 'Fails. Use your own template.'],
          ['130429', 'Rate limit.', 'Retries: 30s, 2m, 10m, 30m, 1h.'],
          ['132000–132016', 'Template problem — wrong params, paused, missing.', 'Fails permanently and raises an alert.'],
          ['131052 / 131053', 'Media too large, or the upload failed.', 'Fails permanently.'],
          ['190 / HTTP 401', 'Access token invalid or expired.', 'Pauses the whole queue and pages someone. P1.'],
          ['141006', 'Payment method problem.', 'Blocks business-initiated messages account-wide.'],
          ['141010', 'Business not verified.', 'Caps you at 250 unique recipients per day.'],
        ]}
      />

      <Callout tone="danger" title="A 200 does not mean delivered">
        Meta returns <Code>200</Code> when it <em>accepts</em> a message. Real delivery status only
        arrives over a webhook. If webhooks are not configured you are flying blind: messages can
        be accepted and silently dropped, which is exactly what an account-level block looks like.
        Check the health panel, not the HTTP status.
      </Callout>
    </Section>
  ),
};

/* ─────────────────────────── Operations ─────────────────────────── */

const operations: DocPage = {
  id: 'operations',
  title: 'Running it',
  group: 'Operations',
  keywords: 'deploy install setup.sh docker upgrade backup restore uninstall secrets rotate',
  render: () => (
    <Section id="operations" title="Running it">
      <P>
        One script handles install and day-to-day management. It runs everything in Docker:
        Caddy, the API, the worker, Postgres, Redis and optionally MinIO.
      </P>
      <CodeBlock lang="bash">{`./setup.sh            # interactive menu
./setup.sh install    # first-time setup
./setup.sh status     # containers + /v1/health
./setup.sh logs worker
./setup.sh upgrade    # backs up, rebuilds, migrates, restarts
./setup.sh backup
./setup.sh restore
./setup.sh uninstall`}</CodeBlock>

      <H3>Secrets</H3>
      <P>
        Install generates the Postgres and Redis passwords, the JWT secret and the webhook verify
        token itself, and preserves them across upgrades. They live only in{' '}
        <Code>wa-service/wa.env</Code> on the host at <Code>chmod 600</Code> — never in the image
        or in git. To rotate one: edit the file, then <Code>./setup.sh restart</Code>.
      </P>
      <Callout tone="warn" title="Backups contain secrets">
        <Code>./setup.sh backup</Code> stores <Code>wa.env</Code> alongside the database dump.
        Treat the backup directory as sensitive.
      </Callout>

      <H3>Webhooks</H3>
      <P>
        Without them you get no delivery receipts and no inbound STOP handling. In the Meta App
        Dashboard set the callback to <Code>https://&lt;host&gt;/webhooks/whatsapp</Code> with the
        verify token from <Code>wa.env</Code>, and subscribe to <Code>messages</Code>,{' '}
        <Code>message_template_status_update</Code>, <Code>phone_number_quality_update</Code>,{' '}
        <Code>business_capability_update</Code> and <Code>account_update</Code>. Confirm{' '}
        <em>Last webhook</em> on Overview starts moving.
      </P>

      <H3>The send allow-list</H3>
      <P>
        <Code>WA_SEND_ALLOWLIST</Code> is the safety valve. When set, the worker fails any message
        to a number not on the list <em>before</em> calling Meta. Keep it populated on staging and
        during a pilot; empty it only when you intend to reach real customers.
      </P>
    </Section>
  ),
};

const runbook: DocPage = {
  id: 'runbook',
  title: 'When something breaks',
  group: 'Operations',
  keywords: 'runbook incident alert p1 p2 queue paused token quality rating failed rate stuck',
  render: () => (
    <Section id="runbook" title="When something breaks">
      <DocTable
        head={['Symptom', 'Severity', 'What to do']}
        rows={[
          [
            'Queue paused, token invalid (190 / 401)',
            'P1',
            'Generate a new system-user token in Business Settings, update wa.env, ./setup.sh restart.',
          ],
          [
            'Messages accepted but nothing arrives',
            'P1',
            'Check Overview → Account health. A BLOCKED WABA usually means a payment problem (141006).',
          ],
          [
            'Everything stuck in queued',
            'P2',
            'Worker is probably down. ./setup.sh status, then ./setup.sh logs worker.',
          ],
          [
            'Failure rate above 10%',
            'P2',
            'Messages page, filter by failed, group by error code. Spikes of 131026 mean bad numbers from the ERP.',
          ],
          [
            'Template PAUSED or REJECTED',
            'P2',
            'Meta pauses templates customers block or report. Rewrite and submit a new version.',
          ],
          [
            'Quality rating drops to Yellow or Red',
            'P2',
            'Stop non-essential sends and review opt-in hygiene. Red for 7 days lowers your tier.',
          ],
          [
            'No webhook for 6 hours while sending',
            'P2',
            'Check the public URL, TLS and the app subscription. Missed statuses are cosmetic; sending still works.',
          ],
          [
            'Unique-recipient cap reached',
            'Info',
            'Expected before business verification. Messages roll to the next day rather than failing.',
          ],
        ]}
      />

      <H3>First three things to check, always</H3>
      <OL>
        <li>Overview → Account health. It names the entity that is blocking.</li>
        <li><Code>./setup.sh logs worker</Code> — the worker logs every decision it makes.</li>
        <li>The failed message&rsquo;s drawer. It carries the raw Meta error code and detail.</li>
      </OL>
    </Section>
  ),
};

const roles: DocPage = {
  id: 'roles',
  title: 'Roles and access',
  group: 'Operations',
  keywords: 'roles admin ops permissions api keys users audit access control',
  render: () => (
    <Section id="roles" title="Roles and access">
      <DocTable
        head={['Capability', 'ops', 'admin']}
        rows={[
          ['View overview, messages, recipients, templates', 'Yes', 'Yes'],
          ['Retry and cancel messages', 'Yes', 'Yes'],
          ['Force-retry a permanent failure', 'No', 'Yes'],
          ['Opt recipients in and out', 'Yes', 'Yes'],
          ['See unmasked phone numbers', 'No', 'Yes'],
          ['Delete a recipient, import CSV', 'No', 'Yes'],
          ['Submit templates to Meta', 'No', 'Yes'],
          ['Manage users and API keys', 'No', 'Yes'],
          ['View the audit log', 'No', 'Yes'],
        ]}
      />
      <Callout tone="info" title="Guards are enforced server-side">
        The dashboard hides what your role cannot do, but the backend checks independently. A
        hidden button is a convenience, not the security boundary.
      </Callout>

      <H3>API keys</H3>
      <P>
        Machine callers use scoped API keys. The raw key is shown exactly once at creation and
        stored only as a hash — if it is lost, revoke it and issue a new one. Give the ERP cron
        only <Code>messages:write</Code> and <Code>recipients:read</Code>.
      </P>
    </Section>
  ),
};

export const DOC_PAGES: DocPage[] = [
  overview,
  account,
  templates,
  sending,
  consent,
  apiRef,
  errors,
  operations,
  runbook,
  roles,
];

export const DOC_GROUPS = [...new Set(DOC_PAGES.map((p) => p.group))];
